import { useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, ScrollRestoration, UNSAFE_RouteContext, useLocation, useNavigate, useRoutes, type Location, type RouteObject } from "react-router";
import { Snackbar } from "@hamlak/ui";
import { registerNavigate } from "@/app/navigation";
import { deepLinkToPath } from "@/app/deepLink";
import { takeColdStartPath } from "@/app/bootstrap";
import { platform } from "@/platform";
import { useUiStore } from "@/stores/uiStore";
import { t } from "@/i18n/ko";
import { abandonOnboarding, isOnboardingIncomplete, ONBOARDING_ROUTES } from "@/app/onboarding";
import { NoticeDialogs } from "@/features/system/NoticeDialogs";
import { HomePage } from "@/features/home/HomePage";
import { PlotPage } from "@/features/plot/PlotPage";
import { ChatPage } from "@/features/chat/ChatPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { SupportPage } from "@/features/settings/SupportPage";
import { usePurchaseRecovery } from "@/features/store/usePurchaseRecovery";
import { usePushBridge } from "@/features/push/usePushBridge";
import { Gnb } from "@/components/Gnb";
import { LAYOUT_VARS, useWidthClass } from "./useWidthClass";
import { BackdropContext } from "./backdrop";
import "./appShell.css";

const SNACKBAR_MS = 3000;

/**
 * PC(large)에서 가운데 모달로 뜨는 라우트(시안 597:2·597:69): 로그인 420, 가입 단계 480. 뒤에는 진입 직전 화면을 어둡게 깔아 둔다(598:217).
 * 로그인은 ESC·바깥 클릭으로 닫고, 가입 단계는 막는다(PC 케이스 정리).
 */
type RouteModal = "login" | "onboarding";
const ROUTE_MODALS: Record<string, RouteModal> = Object.fromEntries([["/login", "login"], ...ONBOARDING_ROUTES.map((r) => [r, "onboarding"])]);

/** 모달 뒤에 깔 수 있는 화면: 비회원이 로그인을 여는 곳. 그 밖(회원 가드 redirect·딥링크 직접 진입 등)은 홈. */
const BACKDROP_ROUTES: RouteObject[] = [
  { path: "/plots/:key", element: <PlotPage /> },
  { path: "/chat/:key", element: <ChatPage /> },
  { path: "/settings", element: <SettingsPage /> },
  { path: "/support", element: <SupportPage /> },
  { path: "*", element: <HomePage /> },
];
/** 배경 location 으로 그 라우트를 그린다(react-router 모달 패턴). 루트 라우트 아래의 하위 Routes 가 아니라 독립 트리로 매칭되도록 RouteContext 를 비운다. */
function RouteBackdrop({ location }: { location: Location | null }) {
  return useRoutes(BACKDROP_ROUTES, location ?? "/home");
}
const EMPTY_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false };

/** 반응형 틀: <html data-width-class> + CSS 변수. 하단 탭 노출은 각 페이지(/home·/chats·/me)가 결정하고, large 에서는 GNB 가 대신한다(CSS 로 숨김). */
export function AppShell() {
  const widthClass = useWidthClass();
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useUiStore((s) => s.snackbar);
  const hideSnackbar = useUiStore((s) => s.hideSnackbar);
  const isLarge = widthClass === "large";
  const routeModal = isLarge ? ROUTE_MODALS[location.pathname] : undefined;
  // 모달 뒤 화면: 로그인을 연 화면(state.background). state 가 없으면(회원 가드 redirect 등) 모달 직전 화면,
  // 그것도 없으면(딥링크로 /login 직접 진입) 홈. 로그인 → 가입 단계로 넘어가도 유지한다
  const stateBackground = (location.state as { background?: Location } | null)?.background ?? null;
  const backgroundRef = useRef<Location | null>(null);
  const background = routeModal ? (stateBackground ?? backgroundRef.current) : null;
  useLayoutEffect(() => {
    backgroundRef.current = routeModal ? background : location;
  }, [routeModal, background, location]);
  // 미처리 구매 복구(AC0-179): 회원이면 시작·포그라운드 복귀 때
  usePurchaseRecovery();
  // 알림 탭 → 채팅방 · 푸시 토큰 등록(AC0-260)
  usePushBridge();

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.widthClass = widthClass;
    for (const [name, value] of Object.entries(LAYOUT_VARS[widthClass])) root.style.setProperty(name, value);
  }, [widthClass]);

  useEffect(() => {
    registerNavigate((to, opts) => navigate(to, opts?.replace ? { replace: true } : undefined));
    return () => registerNavigate(null);
  }, [navigate]);

  // 네이티브 스플래시(launchAutoHide:false)는 보통 SplashPage 가 내리지만, HydrateFallback 없이 바로 셸이 그려지는 경우를 위한 안전장치(AC0-90). 멱등.
  useEffect(() => {
    void platform.app.hideSplash();
  }, []);

  // Android 뒤로가기(AC0-88): 열린 시트가 있으면 맨 위 시트만 닫고, 히스토리가 있으면 뒤로, 첫 화면(홈)이면 앱 종료.
  // idx 는 react-router 가 history.state 에 두는 스택 깊이(루트 리다이렉트는 replace 라 홈이 0).
  useEffect(() => {
    return platform.app.onBackButton(() => {
      const ui = useUiStore.getState();
      if (ui.sheets.length > 0) return ui.closeSheet();
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (idx > 0) return navigate(-1);
      void platform.app.exit();
    });
  }, [navigate]);

  // 부팅이 정한 콜드 스타트 딥링크(Android 디퍼드 딥링크 등, AC0-360): 홈 위에 push 한다(뒤로 = 홈).
  useEffect(() => {
    const path = takeColdStartPath();
    if (path) navigate(path);
  }, [navigate]);

  // 딥링크·앱 링크(AC0-261): hamlak://… · https://<도메인>/… → 라우터 경로. 셸은 부팅 뒤에 그려지므로 콜드 스타트 링크도 홈 위에 쌓인다(뒤로 = 홈).
  useEffect(() => {
    return platform.app.onOpenUrl((url) => {
      const path = deepLinkToPath(url);
      if (path) navigate(path);
    });
  }, [navigate]);

  // 온보딩 이탈 = 로그아웃(AC0-149): 미완 세션으로 온보딩 라우트 밖에 오면 비회원으로 되돌린다. /login 은 로그인 직후 /terms 로 넘어가는 길목이라 제외.
  // 회원 전용 라우트는 가드(requireMember)가 먼저 처리한다.
  useEffect(() => {
    const path = location.pathname;
    if (path === "/login" || (ONBOARDING_ROUTES as readonly string[]).includes(path) || !isOnboardingIncomplete()) return;
    void abandonOnboarding().then((done) => done && useUiStore.getState().showSnackbar(t("onboarding.abandoned")));
  }, [location.pathname]);

  const dismissLogin = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate("/home", { replace: true });
  };
  useEffect(() => {
    if (routeModal !== "login") return;
    const onKey = (e: KeyboardEvent) => {
      // 시트·다이얼로그가 위에 떠 있으면 그쪽이 ESC 를 받는다
      if (e.key === "Escape" && useUiStore.getState().sheets.length === 0 && !document.querySelector('[role="alertdialog"]')) dismissLogin();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // dismissLogin 은 navigate 만 쓴다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeModal]);

  useEffect(() => {
    if (!snackbar) return;
    const id = window.setTimeout(hideSnackbar, SNACKBAR_MS);
    return () => window.clearTimeout(id);
  }, [snackbar, hideSnackbar]);

  return (
    <div className="shell" data-path={location.pathname}>
      {isLarge && <Gnb />}
      {routeModal && (
        <div className="shell-backdrop" inert aria-hidden>
          <BackdropContext.Provider value>
            <UNSAFE_RouteContext.Provider value={EMPTY_ROUTE_CONTEXT}>
              <RouteBackdrop location={background} />
            </UNSAFE_RouteContext.Provider>
          </BackdropContext.Provider>
        </div>
      )}
      {/* 모달 여부와 상관없이 같은 트리 — 1200 경계를 넘어도 페이지가 리마운트되지 않는다. 모달이 아니면 두 겹 모두 display: contents */}
      <div
        className="shell-route"
        data-modal={routeModal}
        onClick={routeModal === "login" ? (e) => e.target === e.currentTarget && dismissLogin() : undefined}
      >
        <div className="shell-route__panel">
          <Outlet />
        </div>
      </div>
      <NoticeDialogs />
      {/* 뒤로가기면 그 화면의 스크롤 위치로, 새 화면이면 맨 위로. iOS 스와이프 뒤로가기는 캡처해 둔 이전 화면을 보여 주다가 실제 화면으로 바꾸므로 위치가 다르면 튄다 */}
      <ScrollRestoration />
      {snackbar && (
        <div className="shell-snackbar">
          <Snackbar message={snackbar.message} tone={snackbar.tone ?? "neutral"} {...(snackbar.action ? { action: snackbar.action } : {})} />
        </div>
      )}
    </div>
  );
}
